import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { SequencesDialog } from '../components/SequencesDialog';
import { ModuleSupportsSection } from '../components/ModuleSupportsSection';
import { ModuleApercuSheet } from '../components/modules/ModuleApercuSheet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  BookOpen,
  Clock,
  Pencil,
  Trash2,
  Layers,
  List,
  Grid3X3,
  Filter,
  X,
  ArrowLeft,
  Award,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  FileText,
  ExternalLink,
  ClipboardList,
  ListOrdered,
  KeyRound
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { getAuthToken } from '../lib/authToken';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ModulesPage = ({ embedded = false, readOnly = false }) => {
  const [modules, setModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sequenceFilter, setSequenceFilter] = useState('all');
  const [sequencesDialogOpen, setSequencesDialogOpen] = useState(false);
  const [dbSequences, setDbSequences] = useState([]);

  const fetchDbSequences = async () => {
    try {
      const resp = await axios.get(`${API_URL}/sequences`);
      setDbSequences(resp.data);
    } catch (err) { /* silencieux */ }
  };
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [sortField, setSortField] = useState('code'); // 'code' or 'titre'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' or 'desc'
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null); // For detail view
  const [qcms, setQcms] = useState([]);
  const [allEvals, setAllEvals] = useState([]);
  const [moduleEvals, setModuleEvals] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [newSeqOpen, setNewSeqOpen] = useState(false);
  const [newSeqData, setNewSeqData] = useState({ code: '', name: '' });

  const handleCreateSequence = async () => {
    const code = newSeqData.code.trim();
    const name = newSeqData.name.trim();
    if (!code || !name) {
      toast.error('Code et nom de la séquence requis');
      return;
    }
    try {
      await axios.post(`${API_URL}/sequences`, { code, name });
      toast.success(`Séquence ${code} créée`);
      fetchDbSequences();
      setFormData(prev => ({ ...prev, sequence_code: code, sequence_name: name, code: prev.code || `${code}-` }));
      setNewSeqOpen(false);
      setNewSeqData({ code: '', name: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création de la séquence');
    }
  };
  
  const [formData, setFormData] = useState({
    code: '',
    titre: '',
    duree_heures: 0,
    contenu: '',
    deroule_pedagogique: '',
    objectifs: '',
    evaluations: [],
    sequence_code: '',
    sequence_name: '',
    certification_id: '',
    tests_lies: []
  });

  // Get unique sequences for filter
  const sequences = useMemo(() => {
    const seqMap = new Map();
    modules.forEach(m => {
      if (m.sequence_code && m.sequence_name) {
        seqMap.set(m.sequence_code, m.sequence_name);
      }
    });
    dbSequences.forEach(s => {
      if (!seqMap.has(s.code)) seqMap.set(s.code, s.name);
    });
    return Array.from(seqMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [modules, dbSequences]);

  useEffect(() => {
    fetchModules();
    fetchDbSequences();
  }, []);

  const fetchModules = async () => {
    try {
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [modRes, qcmRes, certRes, evalRes] = await Promise.all([
        axios.get(`${API_URL}/modules`),
        axios.get(`${API_URL}/qcms`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/certifications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/evaluations-manuelles`, { headers }).catch(() => ({ data: [] })),
      ]);
      setModules(modRes.data);
      setQcms(qcmRes.data);
      setCertifications(certRes.data);
      setAllEvals(evalRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (module = null) => {
    if (module) {
      setEditingModule(module);
      setFormData({
        code: module.code,
        titre: module.titre,
        duree_heures: module.duree_heures || 0,
        contenu: module.contenu || module.description || '',
        deroule_pedagogique: module.deroule_pedagogique || '',
        objectifs: module.objectifs || '',
        evaluations: (module.evaluations || (module.evaluation_type ? [{ type: module.evaluation_type, sous_type: '', details: module.evaluation_details || '' }] : [])).map(ev => ({ _uid: crypto.randomUUID(), ...ev })),
        sequence_code: module.sequence_code || '',
        sequence_name: module.sequence_name || '',
        certification_id: module.certification_id || '',
        tests_lies: module.tests_lies || []
      });
    } else {
      setEditingModule(null);
      setFormData({
        code: '',
        titre: '',
        duree_heures: 0,
        contenu: '',
        deroule_pedagogique: '',
        objectifs: '',
        evaluations: [],
        sequence_code: '',
        sequence_name: '',
        certification_id: '',
        tests_lies: []
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, evaluations: (formData.evaluations || []).map(({ _uid, ...ev }) => ev) };
    try {
      if (editingModule) {
        await axios.put(`${API_URL}/modules/${editingModule.id}`, payload);
        toast.success('Module mis à jour');
      } else {
        await axios.post(`${API_URL}/modules`, payload);
        toast.success('Module créé');
      }
      setDialogOpen(false);
      fetchModules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/modules/${deleteId}`);
      setModules(modules.filter(m => m.id !== deleteId));
      // Close detail view if the deleted module was selected
      if (selectedModule && selectedModule.id === deleteId) {
        setSelectedModule(null);
      }
      toast.success('Module supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteId(null);
    }
  };

  // Open detail view
  const handleSelectModule = async (module) => {
    setSelectedModule(module);
    try {
      const res = await axios.get(`${API_URL}/evaluations-manuelles/by-module/${module.id}`);
      setModuleEvals(res.data);
    } catch {
      setModuleEvals([]);
    }
  };

  // Edit from detail view
  const handleEditFromDetail = () => {
    if (selectedModule) {
      setSelectedModule(null); // Close detail dialog first
      handleOpenDialog(selectedModule);
    }
  };

  // Delete from detail view  
  const handleDeleteFromDetail = () => {
    if (selectedModule) {
      setDeleteId(selectedModule.id);
    }
  };

  const filteredModules = modules.filter(module => {
    const matchesSearch = module.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      module.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSequence = sequenceFilter === 'all' || module.sequence_code === sequenceFilter;
    return matchesSearch && matchesSequence;
  }).sort((a, b) => {
    const valA = (a[sortField] || '').toLowerCase();
    const valB = (b[sortField] || '').toLowerCase();
    const cmp = valA.localeCompare(valB, 'fr', { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const content = (
      <div className="space-y-6" data-testid="modules-page">
        {/* Header */}
        {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Modules</h1>
            <p className="text-slate-500 mt-1">{modules.length} module(s)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setSequencesDialogOpen(true)} data-testid="btn-manage-sequences">
              <ListOrdered className="w-4 h-4 mr-2" />
              Séquences
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-module-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau module
            </Button>
          </div>
        </div>
        )}
        {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{modules.length} module(s)</p>
          {!readOnly && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setSequencesDialogOpen(true)} className="gap-1" data-testid="btn-manage-sequences">
                <ListOrdered className="w-4 h-4" /> Séquences
              </Button>
              <Button size="sm" onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="new-module-btn">
                <Plus className="w-4 h-4" /> Nouveau module
              </Button>
            </div>
          )}
        </div>
        )}

        <SequencesDialog
          open={sequencesDialogOpen}
          onOpenChange={setSequencesDialogOpen}
          onChanged={() => { fetchModules(); fetchDbSequences(); }}
        />

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher un module..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-modules"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={sequenceFilter} onValueChange={setSequenceFilter}>
              <SelectTrigger className="w-[280px]" data-testid="sequence-filter">
                <SelectValue placeholder="Filtrer par séquence" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les séquences</SelectItem>
                {sequences.map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    {code} - {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-1 border rounded-lg p-1">
            <Button 
              variant={viewMode === 'table' ? 'secondary' : 'ghost'} 
              size="icon"
              onClick={() => setViewMode('table')}
              className="h-8 w-8"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
              size="icon"
              onClick={() => setViewMode('grid')}
              className="h-8 w-8"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Modules Display */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 rounded w-1/4 mb-4" />
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredModules.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun module</h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || sequenceFilter !== 'all' ? 'Aucun résultat pour cette recherche' : 'Créez votre premier module'}
              </p>
              {!searchTerm && sequenceFilter === 'all' && (
                <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un module
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
          /* Table View */
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th
                      className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3 cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => toggleSort('code')}
                      data-testid="sort-code"
                    >
                      <span className="inline-flex items-center gap-1">
                        Code
                        {sortField === 'code' ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                      </span>
                    </th>
                    <th
                      className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3 cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => toggleSort('titre')}
                      data-testid="sort-titre"
                    >
                      <span className="inline-flex items-center gap-1">
                        Module
                        {sortField === 'titre' ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                      </span>
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">Séquence</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3 w-20">Durée</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredModules.map((module) => (
                    <tr 
                      key={module.id} 
                      className="hover:bg-indigo-50/50 transition-colors cursor-pointer" 
                      data-testid={`module-row-${module.id}`}
                      onClick={() => handleSelectModule(module)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-indigo-600 font-medium">{module.code}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 flex items-center gap-1.5">
                          {module.titre}
                          {module.certification_id && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-700" data-testid={`badge-certifiant-${module.id}`}>Certifiant</span>
                          )}
                        </p>
                        {(module.contenu || module.description) && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{module.contenu || module.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {module.sequence_code && (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                              {module.sequence_code}
                            </span>
                            <span className="text-sm text-slate-500 truncate max-w-[200px]">{module.sequence_name}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{module.duree_heures}h</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-slate-50 border-t text-sm text-slate-500">
              {filteredModules.length} module(s) affiché(s)
            </div>
          </Card>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredModules.map((module) => (
              <Card 
                key={module.id} 
                className="hover:shadow-lg hover:border-indigo-200 transition-all border-0 shadow-sm cursor-pointer"
                data-testid={`module-card-${module.id}`}
                onClick={() => handleSelectModule(module)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-mono text-indigo-600 font-medium">{module.code}</span>
                      <CardTitle className="text-lg mt-1 font-['Plus_Jakarta_Sans']">{module.titre}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {module.sequence_code && (
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-slate-400" />
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        {module.sequence_code}
                      </span>
                      <span className="text-xs text-slate-500 truncate">{module.sequence_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>{module.duree_heures}h</span>
                  </div>
                  {(module.contenu || module.description) && (
                    <p className="text-sm text-slate-500 line-clamp-2">{module.contenu || module.description}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Aperçu du module (panneau latéral) */}
        <ModuleApercuSheet module={selectedModule} qcms={qcms} evals={moduleEvals} certifications={certifications} readOnly={readOnly}
          onClose={() => setSelectedModule(null)} onEdit={handleEditFromDetail} onDelete={handleDeleteFromDetail} />

        {/* Module Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="module-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans']">
                {editingModule ? 'Modifier le module' : 'Nouveau module'}
              </DialogTitle>
              <DialogDescription>
                {editingModule ? 'Modifiez les informations du module' : 'Créez un nouveau module'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-end -mt-2">
                <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-module-top">
                  {editingModule ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
              {/* 1. Séquence d'abord */}
              <div className="space-y-2">
                <Label>Séquence *</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.sequence_code || 'none'}
                    onValueChange={(v) => {
                      if (v === 'none') {
                        setFormData({ ...formData, sequence_code: '', sequence_name: '' });
                        return;
                      }
                      const match = sequences.find(([c]) => c === v);
                      setFormData(prev => ({
                        ...prev,
                        sequence_code: v,
                        sequence_name: match ? match[1] : '',
                        code: (!prev.code || prev.code === `${prev.sequence_code}-`) ? `${v}-` : prev.code,
                      }));
                    }}
                  >
                    <SelectTrigger className="flex-1" data-testid="select-module-sequence">
                      <SelectValue placeholder="Choisir une séquence..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="none">— Aucune séquence</SelectItem>
                      {sequences.map(([code, name]) => (
                        <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="flex-shrink-0"
                    title="Créer une nouvelle séquence"
                    onClick={() => { setNewSeqData({ code: '', name: '' }); setNewSeqOpen(true); }}
                    data-testid="btn-new-sequence"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* 2. Code + Durée */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder={formData.sequence_code ? `Ex: ${formData.sequence_code}-001` : 'Ex: MOD-001'}
                    required
                    data-testid="input-module-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duree_heures">Durée (heures)</Label>
                  <Input
                    id="duree_heures"
                    type="number"
                    value={formData.duree_heures}
                    onChange={(e) => setFormData({ ...formData, duree_heures: parseInt(e.target.value) || 0 })}
                    min="0"
                    data-testid="input-module-duree"
                  />
                </div>
              </div>

              {/* Modules existants de la séquence */}
              {formData.sequence_code && (() => {
                const existing = modules
                  .filter(m => m.sequence_code === formData.sequence_code && (!editingModule || m.id !== editingModule.id))
                  .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
                return (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 -mt-2" data-testid="existing-modules-hint">
                    <p className="text-[11px] font-medium text-slate-500 mb-1.5">
                      {existing.length > 0
                        ? `${existing.length} module(s) existant(s) dans la séquence ${formData.sequence_code} — choisissez un numéro libre :`
                        : `Aucun module dans la séquence ${formData.sequence_code} pour l'instant`}
                    </p>
                    {existing.length > 0 && (
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                        {existing.map(m => (
                          <span key={m.id} className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px] text-indigo-600" title={m.titre}>
                            {m.code}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 3. Titre */}
              <div className="space-y-2">
                <Label htmlFor="titre">Titre *</Label>
                <Textarea
                  id="titre"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  placeholder="Ex: Introduction à Excel"
                  rows={2}
                  required
                  data-testid="input-module-titre"
                />
              </div>

              <div className="space-y-2">
                <Label>Certification préparée (module certifiant)</Label>
                <Select
                  value={formData.certification_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, certification_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger
                    className="h-auto min-h-9 py-2 whitespace-normal text-left [&>span]:whitespace-normal [&>span]:line-clamp-none"
                    data-testid="select-module-certification"
                  >
                    <SelectValue placeholder="Aucune certification" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucune (module non certifiant)</SelectItem>
                    {certifications.map(c => (
                      <SelectItem key={c.id} value={c.id} className="whitespace-normal">{c.code_rncp} — {c.titre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400">Une formation contenant au moins un module certifiant devient automatiquement certifiante</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="objectifs">Objectifs pédagogiques</Label>
                <Textarea
                  id="objectifs"
                  value={formData.objectifs}
                  onChange={(e) => setFormData({ ...formData, objectifs: e.target.value })}
                  placeholder="Ce que le stagiaire doit savoir / savoir-faire à l'issue du module..."
                  rows={3}
                  data-testid="input-module-objectifs"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contenu">Contenu</Label>
                <Textarea
                  id="contenu"
                  value={formData.contenu}
                  onChange={(e) => setFormData({ ...formData, contenu: e.target.value })}
                  placeholder="Décrivez le contenu du module (thèmes abordés, notions, savoir-faire travaillés)..."
                  rows={4}
                  data-testid="input-module-contenu"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="deroule_pedagogique">Déroulé pédagogique</Label>
                <p className="text-[11px] text-slate-400 -mt-1">Expliquez au formateur comment utiliser ce module : enchaînement des séquences, méthodes d'animation, supports à mobiliser, exercices, points de vigilance…</p>
                <Textarea
                  id="deroule_pedagogique"
                  value={formData.deroule_pedagogique}
                  onChange={(e) => setFormData({ ...formData, deroule_pedagogique: e.target.value })}
                  placeholder="Ex. : 1) Accueil et rappel des objectifs (15 min) — support Diaporama S1. 2) Démonstration… 3) Mise en pratique par binômes avec la fiche exercice E2…"
                  rows={6}
                  data-testid="input-module-deroule"
                />
              </div>

              {/* Supports pédagogiques : ajout uniquement depuis la modification */}
              <div className="space-y-2 rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid="module-edit-supports">
                <Label>Supports pédagogiques</Label>
                {editingModule ? (
                  <ModuleSupportsSection moduleId={editingModule.id} canManage={!readOnly} />
                ) : (
                  <p className="text-xs text-slate-400 italic">Créez d'abord le module, puis ajoutez ses supports depuis « Modifier ».</p>
                )}
              </div>

              {/* Tests rattachés (QCM & Évaluations) */}
              <div className="space-y-2" data-testid="tests-lies-section">
                <Label>Tests rattachés (QCM & Évaluations)</Label>
                <p className="text-[11px] text-slate-400">Activez « Test clé » : si un candidat obtient toutes les compétences « Acquis » sur ce test, le module est validé automatiquement pour lui.</p>
                {(formData.tests_lies || []).length > 0 && (
                  <div className="space-y-1.5">
                    {formData.tests_lies.map((t, idx) => {
                      const test = t.test_type === 'qcm'
                        ? qcms.find(q => q.id === t.test_id)
                        : allEvals.find(e => e.id === t.test_id);
                      const label = test
                        ? (t.test_type === 'qcm' ? test.titre : `${test.code ? test.code + ' — ' : ''}${test.titre}`)
                        : 'Test introuvable';
                      return (
                        <div key={t.test_id} className={`flex items-center gap-2 p-2 rounded-lg border ${t.is_cle ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`} data-testid={`test-lie-row-${t.test_id}`}>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 ${t.test_type === 'qcm' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>
                            {t.test_type === 'qcm' ? 'QCM' : 'ÉVAL'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-700 truncate">{label}</p>
                            {test && (test.competences_visees || []).length > 0 && (
                              <p className="text-[10px] text-slate-400">{test.competences_visees.length} savoir-faire</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <KeyRound className={`w-3.5 h-3.5 ${t.is_cle ? 'text-amber-600' : 'text-slate-300'}`} />
                            <span className="text-[10px] text-slate-500">Test clé</span>
                            <Switch
                              checked={!!t.is_cle}
                              onCheckedChange={(checked) => setFormData(prev => ({
                                ...prev,
                                tests_lies: prev.tests_lies.map((x, i) => i === idx ? { ...x, is_cle: checked } : x),
                              }))}
                              data-testid={`switch-cle-${t.test_id}`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, tests_lies: prev.tests_lies.filter((_, i) => i !== idx) }))}
                            className="p-1 text-red-400 hover:text-red-600 flex-shrink-0"
                            data-testid={`remove-test-lie-${t.test_id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(() => {
                  const linkedIds = new Set((formData.tests_lies || []).map(t => t.test_id));
                  const options = [
                    ...qcms.filter(q => !linkedIds.has(q.id)).map(q => ({
                      id: q.id, type: 'qcm', seq: q.sequence_code || '',
                      label: `[QCM] ${q.sequence_code ? q.sequence_code + ' · ' : ''}${q.titre}`,
                    })),
                    ...allEvals.filter(e => !linkedIds.has(e.id)).map(e => ({
                      id: e.id, type: 'evaluation', seq: e.sequence_code || '',
                      label: `[ÉVAL] ${e.code ? e.code + ' · ' : ''}${e.titre}`,
                    })),
                  ].sort((a, b) => {
                    const aMatch = a.seq === formData.sequence_code ? 0 : 1;
                    const bMatch = b.seq === formData.sequence_code ? 0 : 1;
                    return aMatch - bMatch || a.label.localeCompare(b.label);
                  });
                  return (
                    <Select
                      value="_add"
                      onValueChange={(v) => {
                        if (v === '_add') return;
                        const opt = options.find(o => `${o.type}:${o.id}` === v);
                        if (opt) {
                          setFormData(prev => ({ ...prev, tests_lies: [...(prev.tests_lies || []), { test_id: opt.id, test_type: opt.type, is_cle: false }] }));
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="select-add-test-lie">
                        <SelectValue placeholder="+ Rattacher un test..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="_add">+ Rattacher un test...</SelectItem>
                        {options.map(o => (
                          <SelectItem key={`${o.type}:${o.id}`} value={`${o.type}:${o.id}`} className="text-xs">
                            {o.label}{o.seq && o.seq === formData.sequence_code ? ' ✓' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Évaluations</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setFormData(prev => ({ ...prev, evaluations: [...prev.evaluations, { _uid: crypto.randomUUID(), type: '', sous_type: '', details: '' }] }))}
                    data-testid="btn-add-evaluation"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>

                {formData.evaluations.length === 0 && (
                  <p className="text-xs text-slate-400">Aucune évaluation définie</p>
                )}
                <div className="space-y-2">
                  {formData.evaluations.map((ev, idx) => (
                    <div key={ev._uid || idx} className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg border" data-testid={`evaluation-row-${idx}`}>
                      <div className="flex-1 space-y-1.5">
                        <Select
                          value={ev.type || 'none'}
                          onValueChange={(v) => {
                            const updated = [...formData.evaluations];
                            updated[idx] = { ...updated[idx], type: v === 'none' ? '' : v, sous_type: v !== 'qcm' ? '' : updated[idx].sous_type };
                            setFormData(prev => ({ ...prev, evaluations: updated }));
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Type d'évaluation..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sélectionner...</SelectItem>
                            <SelectItem value="qcm">QCM (Google Form)</SelectItem>
                            <SelectItem value="cas_pratique">Cas pratique</SelectItem>
                            <SelectItem value="examen_certification">Examen de certification</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                        {ev.type === 'qcm' && (
                          <Select
                            value={ev.sous_type || 'none'}
                            onValueChange={(v) => {
                              const updated = [...formData.evaluations];
                              updated[idx] = { ...updated[idx], sous_type: v === 'none' ? '' : v };
                              setFormData(prev => ({ ...prev, evaluations: updated }));
                            }}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Type de QCM..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Type de QCM...</SelectItem>
                              <SelectItem value="positionnement">QCM de positionnement</SelectItem>
                              <SelectItem value="final">QCM final</SelectItem>
                              <SelectItem value="test_blanc">Test blanc</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        <Input
                          value={ev.details || ''}
                          onChange={(e) => {
                            const updated = [...formData.evaluations];
                            updated[idx] = { ...updated[idx], details: e.target.value };
                            setFormData(prev => ({ ...prev, evaluations: updated }));
                          }}
                          placeholder={ev.type === 'qcm' ? 'Lien Google Form...' : 'Précisez...'}
                          className="h-7 text-xs"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, evaluations: prev.evaluations.filter((_, i) => i !== idx) }))}
                        className="p-1 text-red-400 hover:text-red-600 mt-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-module">
                  {editingModule ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Nouvelle séquence */}
        <Dialog open={newSeqOpen} onOpenChange={setNewSeqOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Nouvelle séquence</DialogTitle>
              <DialogDescription>Créez une séquence puis rattachez-y votre module</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Code (acronyme) *</Label>
                <Input
                  value={newSeqData.code}
                  onChange={(e) => setNewSeqData({ ...newSeqData, code: e.target.value.toUpperCase() })}
                  placeholder="Ex: SEC-090"
                  data-testid="input-newseq-code"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nom *</Label>
                <Input
                  value={newSeqData.name}
                  onChange={(e) => setNewSeqData({ ...newSeqData, name: e.target.value })}
                  placeholder="Ex: Sécurité incendie"
                  data-testid="input-newseq-name"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewSeqOpen(false)}>Annuler</Button>
              <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateSequence} data-testid="btn-save-newseq">
                Créer la séquence
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce module ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  );

  return embedded ? content : <DashboardLayout>{content}</DashboardLayout>;
};
